import { PlatformCodeEnum } from "__generated__/graphql";

/**
 * 平台分類 enum（原置於 `components/menu.ts`，階段 2 移入 registry 作為單一資料來源，
 * 並打破 menu ↔ platforms 的循環依賴）。`ALL` 為「Show All」分頁用，非實體平台。
 */
export enum ProductType {
  ALL = 0,
  ACEWIN = 1,
  PG = 2,
  JDB = 3,
  PLAY8 = 4,
  SEXYBCRT = 5,
  PP = 6,
  GO_WIN = 7,
  COMBO = 8,
  GAME_CONVERGE = 9,
  AJOY_PLAY = 10,
}

/**
 * 大廳平台單一資料來源（platform registry）。
 *
 * 取代原本散落於 7+ 處的平台資料：
 *   - `containers/Product/Screen/ProductAll.tsx`（大廳卡片）
 *   - `components/menu.ts`（menuTabs）
 *   - `routes/MainRoutes.tsx`（Path + route children）
 *   - `containers/MainLayout/Header/Header.tsx`（手機漢堡選單）
 *   - `containers/ProductICG/*.tsx`（9 個子頁）
 *   - `components/ProductList/*`（共用元件）
 *
 * ⚠️ 圖片欄位一律存「`assets/images/` 下的完整檔名，含副檔名」。
 *    不可用規則由平台名推導 —— 實際命名無規律，且有 `.jpg` 例外（SEXYBCRT 大廳背景）、
 *    同平台子頁與大廳用不同檔（JDB、PP、COMBO）。所有值皆逐檔 audit 自原始碼。
 *
 *    ⮑ 既有子頁元件（`ProductList` / `ProductListChildren`）目前吃「不含副檔名」字串、
 *       自行補 `.png`（`assets/images/${imageName}.png`）。階段 3/4 接線時，元件需改成
 *       直接吃此處的完整檔名（移除元件內寫死的 `.png`），否則 `.jpg` 例外會壞。
 *
 * 各介面顯示的平台集合**不一致**，故用 per-surface 旗標（`showInLobby` / `showInMenu` /
 * `hasSubPage`），不可用單一陣列無腦衍生：
 *   - 大廳卡片：10 平台 + Pinnacle
 *   - menu / 漢堡：10 平台（無 Pinnacle）
 *   - 子頁 / route：10 平台（Pinnacle 無 route、無子頁）
 *
 * 陣列順序 = 三處介面的顯示順序（大廳卡片、menuTabs、漢堡選單實測一致）。
 */
export interface PlatformConfig {
  /** 對應舊 `ProductType` enum；Pinnacle 無對應值，給 `null`。 */
  type: ProductType | null;
  /** GraphQL launchGame / gameInfos 用的平台代碼。 */
  platformCode: PlatformCodeEnum;

  // ── 路由 / 文字 ──────────────────────────────────────────────
  /** 子頁路由路徑；無子頁者（Pinnacle）為 `null`。 */
  path: string | null;
  /** menu / 漢堡選單顯示字（注意 GOWIN 全大寫，與子頁 `title` 的 GoWin 不同）。 */
  menuTitle: string | null;
  /** 子頁大標題（`ProductList` title）。 */
  title: string | null;
  /** 子頁介紹文（content-left）。 */
  introduceText: string | null;
  /** SEO schema / meta description。 */
  description: string | null;

  // ── 圖片（完整檔名含副檔名）─────────────────────────────────────
  /** 子頁 content-right 主視覺 logo。 */
  logoImage: string | null;
  /** 子頁背景大圖。 */
  bgImage: string | null;
  /** 子頁遊戲卡片底部 logo（`ProductListChildren` 的 imageName）。 */
  childLogoImage: string | null;
  /** 大廳卡片背景圖（`gr-background`）。 */
  lobbyBgImage: string;
  /** 大廳卡片底部 logo（`gr-bottom-background`）。 */
  lobbyLogoImage: string;

  // ── 分面可見性 ──────────────────────────────────────────────
  showInLobby: boolean;
  showInMenu: boolean;
  hasSubPage: boolean;

  // ── Pinnacle 特例 ──────────────────────────────────────────
  /** `true` = 大廳卡片點擊直接 launchGame，而非 `<Link>` 導頁。 */
  launchDirectly?: boolean;
  /** `launchDirectly` 時帶入的 gameId。 */
  gameId?: string;
}

export const PLATFORMS: readonly PlatformConfig[] = [
  {
    type: ProductType.GAME_CONVERGE,
    platformCode: PlatformCodeEnum.GameConvergeSw,
    path: "/products-game-converge-gaming",
    menuTitle: "GameConverge",
    title: "GameConverge",
    introduceText:
      "Redefining the Art of the Spin. Don't just build slots; we build moments of pure adrenaline. We meticulously craft every symbol and soundscape to ensure that when the reels stop, your excitement begins.",
    description:
      "GameConverge meticulously craft every symbol and soundscape to ensure that when the reels stop.",
    logoImage: "game_converge_logo.png",
    bgImage: "game_converge_bg.png",
    childLogoImage: "game_converge_logo2.png",
    lobbyBgImage: "game_converge_play.png",
    lobbyLogoImage: "game_converge_logo.png",
    showInLobby: true,
    showInMenu: true,
    hasSubPage: true,
  },
  {
    // 平台代碼是 ACEWIN_US_SW，但品牌名為 AJOY PLAY —— 與 ACEWIN（ACEWIN_SW）是不同平台。
    type: ProductType.AJOY_PLAY,
    platformCode: PlatformCodeEnum.AcewinUsSw,
    path: "/products-ajoyplay-gaming",
    menuTitle: "AJOY PLAY",
    title: "AJOY PLAY",
    introduceText:
      "Redefining the Essence of Play. We don't just design games; we craft moments of pure joy. Every mechanic and visual detail is meticulously tuned to ensure that the second you launch AJOY PLAY, your excitement begins.",
    description:
      "We craft moments of pure joy. Every mechanic and visual detail is meticulously tuned to ensure that the second you launch AJOY PLAY, your excitement begins.",
    logoImage: "ajoyplay_logo.png",
    bgImage: "ajoyplay_bg.png",
    childLogoImage: "ajoyplay_logo.png",
    lobbyBgImage: "ajoyplay_play.png",
    lobbyLogoImage: "ajoyplay_logo.png",
    showInLobby: true,
    showInMenu: true,
    hasSubPage: true,
  },
  {
    type: ProductType.ACEWIN,
    platformCode: PlatformCodeEnum.AcewinSw,
    path: "/products-acewin-gaming",
    menuTitle: "ACEWIN",
    title: "ACEWIN",
    introduceText:
      "Enjoy the safe and secure game experience, where you can play online slots, fish, arcade, and many more online games!",
    description:
      "ACEWIN features over 50 slot themes and 7 exciting fishing games, with a total of around 70 games to pick from.",
    logoImage: "acewin_logo.png",
    bgImage: "icg_gaming_bg.png",
    childLogoImage: "acewin_logo.png",
    lobbyBgImage: "gc_game_play.png",
    lobbyLogoImage: "acewin_logo.png",
    showInLobby: true,
    showInMenu: true,
    hasSubPage: true,
  },
  {
    type: ProductType.COMBO,
    platformCode: PlatformCodeEnum.ComboSw,
    path: "/products-combo-gaming",
    menuTitle: "COMBO",
    title: "COMBO",
    introduceText:
      "Welcome to COMBO, the premier destination for high-octane online slots. We believe one win is just the beginning—our platform is built for the streak. Experience seamless gameplay, ultimate security, and the thrill of the stack. Don't just win, hit a COMBO. Join now and let the reels roll!",
    description:
      "Welcome to COMBO, the premier destination for high-octane online slots. We believe one win is just the beginning—our platform is built for the streak. Experience seamless gameplay, ultimate security, and the thrill of the stack. Don't just win, hit a COMBO. Join now and let the reels roll!",
    logoImage: "combo_logo.png",
    bgImage: "combo_bg.png",
    childLogoImage: "combo_logo_product.png",
    lobbyBgImage: "combo_play.png",
    lobbyLogoImage: "combo_logo_product.png",
    showInLobby: true,
    showInMenu: true,
    hasSubPage: true,
  },
  {
    type: ProductType.GO_WIN,
    platformCode: PlatformCodeEnum.GoWinSw,
    path: "/products-go-win-gaming",
    menuTitle: "GOWIN",
    title: "GoWin",
    introduceText:
      "Experience the ultimate thrill at GoWin, your premier destination for high-octane online slots! Every spin brings you closer to the big win. Safe, secure, and packed with endless excitement—join GoWin today and let the reels decide your fortune!",
    description:
      "GOWIN features over 7 slot themes and 3 exciting arcade games, with a total of around 10 games to pick from.",
    logoImage: "go_win_logo.png",
    bgImage: "go_win_bg.png",
    childLogoImage: "go_win_logo.png",
    lobbyBgImage: "go_win_play.png",
    lobbyLogoImage: "go_win_logo.png",
    showInLobby: true,
    showInMenu: true,
    hasSubPage: true,
  },
  {
    type: ProductType.PG,
    platformCode: PlatformCodeEnum.PgSwA,
    path: "/products-pg-gaming",
    menuTitle: "PG",
    title: "PG",
    introduceText:
      "As a game changer in the mobile gaming industry, PG SOFT™ has made a massive impact on the gaming world since its inception in 2015.",
    description:
      "As a game changer in the mobile gaming industry, PG has made a massive impact on the gaming world since its inception in 2015.",
    logoImage: "PGSOFT_logo.png",
    bgImage: "pg_bg.png",
    childLogoImage: "PGSOFT_logo.png",
    lobbyBgImage: "battleground-royale_web_banner.png",
    lobbyLogoImage: "PGSOFT_logo.png",
    showInLobby: true,
    showInMenu: true,
    hasSubPage: true,
  },
  {
    type: ProductType.JDB,
    platformCode: PlatformCodeEnum.JdbSwA,
    path: "/products-jdb-gaming",
    menuTitle: "JDB",
    title: "JDB",
    introduceText:
      "Diverse online entertainment solutions, fish shooting games, slot games, card games, and bingos to various needs and provide you with the most exciting and memorable gaming experience!",
    description:
      "Diverse online entertainment solutions. Provide you with the most exciting and memorable gaming experience!",
    logoImage: "JDB_logo_rainbow.png",
    bgImage: "kingkong_bg.png",
    childLogoImage: "JDB_logo_rainbow.png",
    lobbyBgImage: "Crazy-King-Kong.png",
    // ⚠️ 大廳底 logo 與子頁 logo 不同檔：大廳用 JDB_logo，子頁用 JDB_logo_rainbow。
    lobbyLogoImage: "JDB_logo.png",
    showInLobby: true,
    showInMenu: true,
    hasSubPage: true,
  },
  {
    type: ProductType.PLAY8,
    platformCode: PlatformCodeEnum.Play8SwA,
    path: "/products-play8-gaming",
    menuTitle: "PLAY8",
    title: "PLAY8",
    introduceText:
      "Play8 offers a wide range of styles of fishing shooting games, slot games that are sure to hit your preferred choice.",
    description:
      "Play8 offers a wide range of styles of fishing shooting games, slot games that are sure to hit your preferred choice.",
    logoImage: "play8_logo.png",
    bgImage: "play8_bg.png",
    childLogoImage: "play8_logo.png",
    lobbyBgImage: "Play8-GameIcon.png",
    lobbyLogoImage: "play8_logo.png",
    showInLobby: true,
    showInMenu: true,
    hasSubPage: true,
  },
  {
    type: ProductType.SEXYBCRT,
    platformCode: PlatformCodeEnum.SexybcrtSwA,
    path: "/products-sexybcrt-gaming",
    menuTitle: "SEXYBCRT",
    title: "SEXYBCRT",
    introduceText:
      "Sexybcrt is an online game and can be played by any individual with an internet connection. Come with aesthetically pleasing graphics making the game more enticing each round.",
    description:
      "Sexybcrt is an online game and can be played by any individual with an internet connection. Come with aesthetically pleasing graphics making the game more enticing each round.",
    logoImage: "Sexy_Logo.png",
    bgImage: "Sexy_BG.png",
    childLogoImage: "Sexy_Logo.png",
    // ⚠️ 全站唯一的 .jpg 大廳背景圖。
    lobbyBgImage: "SexyBCRT-GameIcon.jpg",
    lobbyLogoImage: "Sexy_Logo.png",
    showInLobby: true,
    showInMenu: true,
    hasSubPage: true,
  },
  {
    type: ProductType.PP,
    platformCode: PlatformCodeEnum.PpSwA,
    path: "/products-pp-gaming",
    menuTitle: "PP",
    title: "PP",
    introduceText:
      "Pragmatic Play is a leading game developer providing player-favourites to the most successful global brands in the iGaming industry. Offer a multi-product portfolio of slots, live casino, bingo and more, available in all major regulated markets.",
    description:
      "Pragmatic Play is a leading game developer providing player-favourites to the most successful global brands in the iGaming industry.",
    logoImage: "PP_Logo_On-Black.png",
    bgImage: "PP_BG.png",
    childLogoImage: "PP_Logo_On-Black.png",
    lobbyBgImage: "PP-GameIcon.png",
    // ⚠️ 大廳底 logo 與子頁 logo 不同檔：大廳用 PP_Logo_White，子頁用 PP_Logo_On-Black。
    lobbyLogoImage: "PP_Logo_White.png",
    showInLobby: true,
    showInMenu: true,
    hasSubPage: true,
  },
  {
    // Pinnacle 特例：只在大廳出現，點擊直接 launchGame（無子頁、不進 menu / 漢堡、無 route）。
    type: null,
    platformCode: PlatformCodeEnum.PinnacleSwA,
    path: null,
    menuTitle: null,
    title: null,
    introduceText: null,
    description: null,
    logoImage: null,
    bgImage: null,
    childLogoImage: null,
    lobbyBgImage: "pinnacle_play.png",
    lobbyLogoImage: "pinnacle_logo.png",
    showInLobby: true,
    showInMenu: false,
    hasSubPage: false,
    launchDirectly: true,
    gameId: "PINNACLE-SPORTS-001",
  },
  // FA CHAI（停用）：原為大廳卡片，已隨階段 3 從 ProductAll.tsx 的 JSX 註解一併移除。
  // 若要恢復，在此新增一筆 config（圖：fa_chai.png / logo_fa_chai.png、showInLobby: true，
  // 視需要再給 hasSubPage / showInMenu），即會自動出現在大廳。
] as const;

/** 大廳卡片顯示的平台（10 + Pinnacle）。 */
export const LOBBY_PLATFORMS = PLATFORMS.filter((p) => p.showInLobby);

/** menu / 漢堡選單顯示的平台（10，無 Pinnacle）。 */
export const MENU_PLATFORMS = PLATFORMS.filter((p) => p.showInMenu);

/** 有子頁 / route 的平台（10，無 Pinnacle）。 */
export const SUBPAGE_PLATFORMS = PLATFORMS.filter((p) => p.hasSubPage);
